import type { DocumentVersion } from "@prismicio/editor-fields"
import { type MouseEvent, useState } from "react"

import { useCollaborationDataFlag } from "../../../app/integrations/AmplitudeExperiments"
import type { DocumentVersionStatus } from "../../../app/services/core"
import { getReleaseId } from "../../../app/services/release"
import { useCollaborationState } from "../../collaboration/CollaborationStateProvider"
import { DiscardOrRunActionOnUnsavedChangesDialog } from "../../DocumentHeader/HeaderButtons/BlockNavigationOnUnsavedChangesPrompt"
import { useCreateButton } from "../../DocumentHeader/HeaderButtons/CreateButton"
import { useSaveButton } from "../../DocumentHeader/HeaderButtons/SaveButton"
import { useDocument } from "../../DocumentProvider"
import { useRepositoryReleases } from "../../documentRouting"
import { DocumentVersions } from "./DocumentVersions"
import { useVersionPanel } from "./useVersionPanel"
import {
  getReleaseDisplayInfos,
  useVersionDisplayInfo,
} from "./VersionPanel.utils"

export function VersionPanel() {
  const [modalOpen, setModalOpen] = useState(false)
  const [actionType, setActionType] = useState<
    | {
        status: DocumentVersionStatus
        version: DocumentVersion
      }
    | undefined
  >()

  const {
    local,
    selectVersion,
    localVersionSelection,
    localReleaseSelection,
    versions,
  } = useVersionPanel()

  const repositoryReleases = useRepositoryReleases({
    includeMigrationRelease: true,
  })

  const documentReleaseVersions = versions.filter(
    (version) => version.status === "release",
  )

  const unclassified = useVersionDisplayInfo(versions, "unclassified")
  const published = useVersionDisplayInfo(versions, "published")
  const { plannedReleases, migrationReleases } = getReleaseDisplayInfos(
    repositoryReleases,
    documentReleaseVersions,
  )

  // since we aren't relying on react-router to switch between versions
  // we will block the selection on click instead of blocking route navigation
  function onSelectVersion(version: DocumentVersion) {
    // if we have a local version (draft), we need to show the dialog
    // to warn the user that they are about to discard their changes
    // otherwise, we can just select the version
    if (local) {
      setModalOpen(true)

      // we need to set the action type and version to be able to
      // handle the selection request from the dialog
      // if the user decides to discard their changes
      setActionType({
        status: version.status,
        version,
      })
      return
    }

    selectVersion(version)
  }

  /**
   * Ideally, we should use the regular Link behavior to navigate to the selected version,
   * (which we do for opening the document in a new tab)
   * but that would break animations and break going back from a draft to a saved version.
   * You can read more about it here: https://www.notion.so/prismic/1233bee08669809f9a5bf05943c6f8c7
   */
  function onSelectHandler(version: DocumentVersion) {
    return (e: MouseEvent<HTMLButtonElement>) => {
      if (e.metaKey || e.ctrlKey) return

      e.preventDefault()
      onSelectVersion(version)
    }
  }

  function handleDiscardAndSelect() {
    if (!actionType) return

    selectVersion(actionType.version)

    setActionType(undefined)
  }

  const realTimeDraftLastModifiedDate = useRealTimeDraftLastModifiedDate()

  return (
    <>
      <DocumentVersions
        local={local}
        unclassified={
          unclassified && {
            ...unclassified,
            id: "unclassified",
            header: "Edited",
            selected: localVersionSelection === unclassified.version.version_id,
            onSelect: onSelectHandler(unclassified.version),
            date: realTimeDraftLastModifiedDate ?? unclassified.date,
          }
        }
        published={
          published && {
            ...published,
            id: "published",
            header: "Published",
            selected: localVersionSelection === published.version.version_id,
            onSelect: onSelectHandler(published.version),
          }
        }
        release={
          plannedReleases.length !== 0
            ? {
                id: "release",
                header: "Planned",
                items: plannedReleases.map((release) => {
                  return {
                    title: release.title,
                    subtitle: release.subtitle,
                    selected:
                      localReleaseSelection === getReleaseId(release.version),
                    date: release.date,
                    releaseId: getReleaseId(release.version),
                    onSelect: onSelectHandler(release.version),
                  }
                }),
              }
            : undefined
        }
        migrationRelease={
          migrationReleases.length !== 0
            ? {
                id: "migrationRelease",
                header: "Migration Release",
                items: migrationReleases.map((release) => {
                  return {
                    subtitle: release.subtitle,
                    selected:
                      localReleaseSelection === getReleaseId(release.version),
                    date: release.date,
                    releaseId: getReleaseId(release.version),
                    onSelect: onSelectHandler(release.version),
                  }
                }),
              }
            : undefined
        }
      />
      <DiscardOrRunActionOnUnsavedChangesDialog
        open={modalOpen}
        message="You are about to leave this version, but you have updated this document."
        onOpenChange={setModalOpen}
        onDiscard={handleDiscardAndSelect}
        onSaveSuccess={() => {
          setModalOpen(false)
          setActionType(undefined)
        }}
        action={versions.length > 0 ? useSaveButton : useCreateButton}
      />
    </>
  )
}

function useRealTimeDraftLastModifiedDate() {
  const { draft, status } = useDocument()

  const collaborationState = useCollaborationState()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  // If the user is currently on a draft version, show the live time
  // because with collaboration enabled there's no local draft version
  if (
    status === "unclassified" &&
    collaborationState.enabled &&
    canUseCollaborationData
  ) {
    return draft.editedAt
  }

  return undefined
}
